import { useState, useEffect, useCallback, useRef } from 'react';
import { BLACKLIST_GET, BLACKLIST_SET } from '../config/blacklist';

// Loads the deny-list and exposes matching + admin add/remove.
// Fails OPEN: if the API is unreachable, nobody is blocked (so the app keeps
// working before the PHP is deployed, and an API outage can't lock everyone out).
export function useBlacklist() {
  const [data, setData] = useState({ loaded: false, addresses: [], ids: [] });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(BLACKLIST_GET, { cache: 'no-store' });
      const json = await res.json();
      if (!mounted.current) return;
      setData({
        loaded: true,
        addresses: (json.addresses || []).map((a) => String(a).toLowerCase()),
        ids: (json.ids || []).map((i) => Number(i)),
      });
    } catch {
      // fail open
      if (mounted.current) setData((d) => ({ ...d, loaded: true }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isBlacklisted = useCallback(
    (address, userId) => {
      const a = address ? String(address).toLowerCase() : null;
      const id = userId ? Number(userId) : 0;
      if (a && data.addresses.includes(a)) return true;
      if (id && data.ids.includes(id)) return true;
      return false;
    },
    [data]
  );

  // action: 'add' | 'remove'; type: 'address' | 'id'
  const write = useCallback(
    async (action, type, value, secret) => {
      const res = await fetch(BLACKLIST_SET, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': secret || '' },
        body: JSON.stringify({ action, type, value }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || (res.status === 401 ? 'Wrong admin key.' : 'Request failed.'));
      }
      await refresh();
      return json;
    },
    [refresh]
  );

  return { ...data, isBlacklisted, refresh, write };
}
